/* Theme preference: 'system' | 'dark' | 'light'. */
/* Writes data-theme="dark|light" on <html> to drive [data-theme=...] CSS rules. */
/* 'dark'  → data-theme="dark"   (explicit override) */
/* 'light' → data-theme="light"  (blocks @media dark even when OS is dark) */
/* 'system' + OS dark → data-theme="dark"; OS light → no attribute (CSS @media handles it) */

export type ThemePref = 'system' | 'dark' | 'light';

const KEY = 'naia.theme';

export function getThemePref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'dark' || v === 'light' || v === 'system' ? v : 'system';
  } catch { return 'system'; }
}

function effectiveDark(pref: ThemePref): boolean {
  if (pref === 'dark') return true;
  if (pref === 'light') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function applyTheme(pref: ThemePref): void {
  if (effectiveDark(pref)) {
    document.documentElement.setAttribute('data-theme', 'dark');
  } else if (pref === 'light') {
    /* Explicit light: set data-theme="light" to block @media (prefers-color-scheme: dark) */
    document.documentElement.setAttribute('data-theme', 'light');
  } else {
    /* system + OS light: no attribute; @media :not([data-theme="light"]) won't fire */
    document.documentElement.removeAttribute('data-theme');
  }
}

export function setThemePref(pref: ThemePref): void {
  try { localStorage.setItem(KEY, pref); } catch { /* ignore */ }
  applyTheme(pref);
}

/* Call once at startup. Applies the stored pref and, while in 'system' mode, */
/* keeps the theme in sync with OS changes. */
export function initTheme(): void {
  applyTheme(getThemePref());
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  mq.addEventListener('change', () => {
    if (getThemePref() === 'system') applyTheme('system');
  });
}
